import { createRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ArrowLeft, Check, MessageSquare, X } from 'lucide-react'
import { commentsRoute } from '@/ui/pages/management/comments'
import { useGetInvestigationById } from '@/application/services/investigation.service'
import {
  useApproveComment,
  useDeleteComment,
  useGetCommentsByInvestigation,
} from '@/application/services/comment.service'
import { Button } from '@/ui/components/common/Button'
import { CollapsibleSection } from '@/ui/components/common/CollapsibleSection'

export const commentsModerationRoute = createRoute({
  getParentRoute: () => commentsRoute,
  path: '$investigationId',
  component: CommentsModerationPage,
})

const TRUNCATE_LENGTH = 80

/** Thin route wrapper — extracts `investigationId` from the URL and hands it to the section below. */
export function CommentsModerationPage() {
  const { investigationId } = commentsModerationRoute.useParams()
  return <CommentsModerationSection investigationId={investigationId} />
}

/**
 * Moderation queue for one investigation's comments: only comments still
 * `pending` are listed, each collapsed by default behind a summary line
 * (author, date, truncated content) with "Valider" (approve) / "Rejeter"
 * (delete) actions. A comment leaves the list as soon as it is acted on —
 * the query invalidation the mutations already trigger removes it from
 * `pending` on refetch.
 */
export function CommentsModerationSection({ investigationId }: { investigationId: string }) {
  const { t } = useTranslation()

  const { data: investigation } = useGetInvestigationById(investigationId)
  const { data: comments, isLoading } = useGetCommentsByInvestigation(investigationId)
  const { mutateAsync: approveComment } = useApproveComment(investigationId)
  const { mutateAsync: deleteComment } = useDeleteComment(investigationId)

  const pendingComments = (comments ?? []).filter((comment) => comment.status === 'pending')

  const handleValidate = async (commentId: string) => {
    try {
      await approveComment(commentId)
      toast.success(t('admin.comments_validated_toast'))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const handleReject = async (commentId: string) => {
    try {
      await deleteComment(commentId)
      toast.success(t('admin.comments_rejected_toast'))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/manage/comments">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('common.back')}
          </Button>
        </Link>
        <h1 className="text-xl font-bold">
          {t('admin.comments_moderation_title')}
          {investigation && ` — ${investigation.name}`}
        </h1>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_unused, index) => (
            <div key={index} className="h-14 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : pendingComments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{t('admin.comments_moderation_empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingComments.map((comment) => {
            const truncated =
              comment.content.length > TRUNCATE_LENGTH
                ? `${comment.content.slice(0, TRUNCATE_LENGTH)}…`
                : comment.content
            return (
              <div key={comment.commentId} className="bg-card border border-border rounded-lg px-4">
                <CollapsibleSection
                  title={
                    <span className="font-normal">
                      <span className="font-semibold">{comment.username}</span>
                      {' — '}
                      <span className="text-muted-foreground text-xs">
                        {new Date(comment.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                      {' — '}
                      <span className="text-muted-foreground">{truncated}</span>
                    </span>
                  }
                >
                  <div className="pb-3 space-y-3">
                    <p className="text-sm text-foreground/80 leading-relaxed">{comment.content}</p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        leftIcon={<Check className="w-4 h-4" />}
                        onClick={() => handleValidate(comment.commentId)}
                      >
                        {t('admin.comments_validate')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        leftIcon={<X className="w-4 h-4" />}
                        onClick={() => handleReject(comment.commentId)}
                      >
                        {t('admin.comments_reject')}
                      </Button>
                    </div>
                  </div>
                </CollapsibleSection>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
